/**
 * Keeps a route to your own profile out of anyone else's hands.
 *
 * Guarded in the router rather than the component because a component that
 * renders nothing is a blank page, whereas a redirect puts you somewhere
 * real — and because this also covers someone typing the URL directly.
 *
 * A courtesy, not the enforcement. No endpoint accepts a target other than
 * its authenticated caller, so this is about not offering an action that
 * isn't yours, rather than preventing one.
 *
 * ── Why it takes the resolver ────────────────────────────────────────────
 *
 * It used to read `store.getters.getUser.id` inline, which is empty until
 * fetchUserInfo has run. On a direct load — a bot link, a bookmark, a
 * refresh — the guard therefore decided "not yours" about your own profile
 * and bounced you to the overview. The Customize tab was unreachable except
 * by navigating in from another page.
 *
 * `getUserId` resolves from localStorage when the store is still cold, which
 * is the same fallback the `/profile/me` guard relies on. Both guards now
 * answer "who is signed in" the same way; two answers to that question is
 * what produced the bug.
 *
 * @param {() => string|null} getUserId Resolves the signed-in user's id.
 * @returns {(to: object, from: object, next: Function) => void} A vue-router guard.
 */
export function createOwnProfileGuard(getUserId) {
    return function ownProfileGuard(to, from, next) {
        const ownID = getUserId()
        const id = to.params.id
        // `me` is your own profile by definition — though the parent guard
        // will normally have swapped it for the real id before this runs.
        const isOwn = ownID !== null && (id === 'me' || id === ownID)
        next(isOwn ? undefined : { name: 'ProfileOverview', params: { id } })
    }
}
